// src/contexts/AuthContext.tsx
import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

export enum CompanyType {
  Hosting = 1,
  Customer = 2
}

export interface User {
  userId: number;
  email: string;
  firstName: string;
  lastName: string;
  companyId: number;
  companyName: string;
  companyType: CompanyType;
  role: string;
}

export interface RegisterData {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  registrationCode?: string;
  companyId?: number;
  companyName?: string;
  companyPhone?: string;
  companyAddress?: string;
  companyCity?: string;
  companyPostCode?: string;
}

interface RegisterResponse {
  success: boolean;
  message: string;
  requiresApproval?: boolean;
  requiresEmailConfirmation?: boolean;
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (data: RegisterData) => Promise<RegisterResponse>;
  logout: () => void;
  isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

// Helper function to build headers with API key
const buildAuthHeaders = (includeJwtToken: boolean = false): HeadersInit => {
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
  };

  // Add API key (required by ApiKeyMiddleware)
  const apiKey = process.env.REACT_APP_API_KEY;
  if (apiKey) {
    headers['X-API-Key'] = apiKey;
  } else {
    console.error('API key not found in environment variables');
  }

  // Add JWT token if requested and available
  if (includeJwtToken) {
    const token = localStorage.getItem('token');
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
  }

  return headers;
};

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  useEffect(() => {
    // Check for stored auth data on mount
    const storedToken = localStorage.getItem('token');
    const storedUser = localStorage.getItem('user');

    if (storedToken && storedUser) {
      try {
        const parsedUser: User = JSON.parse(storedUser);
        setToken(storedToken);
        setUser(parsedUser);
      } catch (error) {
        console.error('Error parsing stored user data:', error);
        localStorage.removeItem('token');
        localStorage.removeItem('user');
      }
    }
    setIsLoading(false);
  }, []);

  const login = async (email: string, password: string): Promise<void> => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/Auth/login`, {
        method: 'POST',
        headers: buildAuthHeaders(false), // Don't include JWT for login
        body: JSON.stringify({ email, password }),
      });

      if (!response.ok) {
        const errorData = await response.text();
        throw new Error(errorData || 'Login failed');
      }

      const data = await response.json();
      
      setToken(data.token);
      setUser(data.user);
      
      // Store in localStorage
      localStorage.setItem('token', data.token);
      localStorage.setItem('user', JSON.stringify(data.user));
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  };

  const register = async (data: RegisterData): Promise<RegisterResponse> => {
    try {
      const requestBody = {
        email: data.email,
        password: data.password,
        firstName: data.firstName,
        lastName: data.lastName,
        registrationCode: data.registrationCode,
        companyId: data.companyId,
        companyName: data.companyName,
        companyPhone: data.companyPhone,
        companyAddress: data.companyAddress,
        companyCity: data.companyCity,
        companyPostCode: data.companyPostCode
      };

      const response = await fetch(`${process.env.REACT_APP_API_URL}/Auth/register`, {
        method: 'POST',
        headers: buildAuthHeaders(false), // Don't include JWT for registration
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        const errorData = await response.text();
        throw new Error(errorData || 'Registration failed');
      }

      return await response.json();
    } catch (error) {
      console.error('Registration error:', error);
      throw error;
    }
  };

  const logout = (): void => {
    setUser(null);
    setToken(null);
    localStorage.removeItem('token');
    localStorage.removeItem('user');
  };

  const isAuthenticated = !!token && !!user;

  const value: AuthContextType = {
    user,
    token,
    isLoading,
    login,
    register,
    logout,
    isAuthenticated,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};